import { BrowserModule } from '@angular/platform-browser';
import { NoopAnimationsModule } from '@angular/platform-browser/animations';
import { APP_INITIALIZER, NgModule } from '@angular/core';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { HomeComponent } from './core/_pages/home/home.component';
import { ProfileComponent } from './core/_pages/profile/profile.component';
import { FailedComponent } from './core/_pages/failed/failed.component';
import { NZ_DATE_CONFIG, NZ_I18N } from 'ng-zorro-antd/i18n';
import { en_US } from 'ng-zorro-antd/i18n';
import {NgIf, registerLocaleData} from '@angular/common';
import en from '@angular/common/locales/en';
import { FormsModule } from '@angular/forms';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { SharedModule } from './shared/shared.module';
import { MainComponent } from './layouts/main/main.component';
import { HeaderComponent } from './layouts/main/header/header.component';
import { FooterComponent } from './layouts/main/footer/footer.component';
import { EventMessageBusService } from './core/services/event-message-bus.service';
import { ApplicationHttpService } from './core/services/application-http.service';
import { ApplicationListService } from './features/application/services/application-list.service';
import { ApplicationHistoryService } from './features/application/services/application-history.service';
import { ApplicationWorkflowService } from './features/application/services/application-workflow.service';
import { ApplicationDocumentService } from './features/application/services/application-document.service';
import { ApplicationFeedService } from './features/application/services/application-feed.service';
import { NzIconModule, NZ_ICONS } from "ng-zorro-antd/icon";
import { IconDefinition } from '@ant-design/icons-angular';
import {
  ArrowLeftOutline,
  ArrowRightOutline,
  AuditOutline,
  BankOutline,
  BookOutline,
  CheckCircleOutline,
  CheckOutline,
  CloseCircleOutline,
  CloudServerOutline,
  DisconnectOutline,
  ExclamationCircleOutline,
  FilterOutline,
  FolderOpenOutline,
  InfoCircleOutline,
  MinusCircleOutline,
  PlusCircleOutline,
  ReloadOutline,
  RobotOutline,
  SafetyCertificateOutline,
  StopOutline,
  SwapOutline,
  WarningOutline
} from '@ant-design/icons-angular/icons';
import { NotificationsComponent } from './features/notifications/components/notifications/notifications.component';
import { NotificationService } from './features/notifications/services/notification.service';
import { NotificationViewComponent } from './features/notifications/components/notification-view/notification-view.component';
import { CommunicationRequestsComponent } from './features/communications/components/communication-requests/communication-requests.component';
import { CommunicationSrsModalComponent } from './features/communications/components/communication-modal/communication-modal.component';
import { CommunicationChatComponent } from './features/communications/components/communication-chat/communication-chat.component';
import { CommunicationService } from './features/communications/services/communication.service';
import {NzTypographyComponent} from "ng-zorro-antd/typography";
import {NzFlexDirective} from "ng-zorro-antd/flex";
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzDropDownModule, NzDropdownMenuComponent, NzDropDownDirective } from 'ng-zorro-antd/dropdown';
import { httpInterceptorProviders } from './public/interceptors/http-request.interceptor';
import { CommunicationSubmitRequestComponent } from './features/communications/components/communication-submit-request/communication-submit-request.component';
import { FeatureFlagService } from './core/services/feature-flag.service';

registerLocaleData(en);

/**
 * DR-1119 — statically registered icons for the Public Register migration feature
 * (proposal / work-list / gaps / field-descriptor templates). Registered at the root so the
 * singleton NzIconService resolves them synchronously from memory instead of fetching
 * `/assets/*.svg` over HTTP on every render. The per-render async fetch was the second half of the
 * proposal-page freeze loop; pre-registering removes it. Icons NOT listed here are unaffected and
 * still load dynamically as before — this only adds registrations, it does not change icon loading
 * for other features.
 */
const PR_MIGRATION_ICONS: IconDefinition[] = [
  ArrowLeftOutline,
  ArrowRightOutline,
  AuditOutline,
  BankOutline,
  BookOutline,
  CheckCircleOutline,
  CheckOutline,
  CloseCircleOutline,
  CloudServerOutline,
  DisconnectOutline,
  ExclamationCircleOutline,
  FilterOutline,
  FolderOpenOutline,
  InfoCircleOutline,
  MinusCircleOutline,
  PlusCircleOutline,
  ReloadOutline,
  RobotOutline,
  SafetyCertificateOutline,
  StopOutline,
  SwapOutline,
  WarningOutline
];

@NgModule({ declarations: [
        AppComponent,
        HomeComponent,
        ProfileComponent,
        FailedComponent,
        MainComponent,
        HeaderComponent,
        FooterComponent,
        NotificationsComponent,
        NotificationViewComponent,
        CommunicationRequestsComponent,
        CommunicationSrsModalComponent,
        CommunicationChatComponent,
        CommunicationSubmitRequestComponent
    ],
    bootstrap: [AppComponent],
    imports: [BrowserModule,
        NoopAnimationsModule,
        AppRoutingModule,
        FormsModule,
        SharedModule,
        NzIconModule,
        NzTypographyComponent,
        NzFlexDirective,
        NzAlertModule,
        NzDropDownModule,
        NzDropdownMenuComponent,
        NzDropDownDirective,
        NgIf],
    providers: [
        {
            provide: NZ_I18N,
            useValue: en_US
        },
        provideAnimationsAsync(),
        provideHttpClient(),
        EventMessageBusService,
        ApplicationHttpService,
        ApplicationListService,
        ApplicationHistoryService,
        ApplicationWorkflowService,
        ApplicationDocumentService,
        ApplicationFeedService,
        NotificationService,
        CommunicationService,
        httpInterceptorProviders,
        provideHttpClient(withInterceptorsFromDi()),
        {
          provide: APP_INITIALIZER,
          useFactory: (ffs: FeatureFlagService) => () => ffs.load().toPromise(),
          deps: [FeatureFlagService],
          multi: true,
        },
        {
        provide: NZ_DATE_CONFIG,
        useValue: {
            firstDayOfWeek: 1
        }
        },
        {
            provide: NZ_ICONS,
            useValue: PR_MIGRATION_ICONS
        }
    ] })
export class AppModule { }
